import { useEffect, useState } from "react";

/**
 * Notices when the backend has been redeployed, so a tab left open across a
 * release can offer to reload rather than keep running a frontend that no
 * longer matches the API behind it.
 *
 * The version a page loaded against is fetched from /api/version (the
 * release's sha, see main.py) and kept in localStorage; every 30s it is
 * fetched again, and any difference makes the hook return true. It does not
 * reload by itself: whoever is halfway through something on the page decides
 * when, via the banner App renders.
 *
 * The comparison is against the version *this page* loaded with, held in
 * memory, not against whatever localStorage holds when the poll fires. Tabs
 * share localStorage, so the first tab to reload after a deploy writes the
 * new version there, and a second tab comparing against storage would then
 * see a match and never show the banner.
 */
const STORAGE_KEY = "backend_version";
const POLL_MS = 30_000;

async function fetchVersion(apiUrl: string): Promise<string | null> {
  try {
    // no-store: a cached answer would hide exactly the change this looks for.
    const response = await fetch(`${apiUrl}/api/version`, { cache: "no-store" });
    if (!response.ok) return null;
    const data = await response.json();
    return typeof data.version === "string" ? data.version : null;
  } catch {
    // Mid-deploy the backend is briefly down. That's not a new version, so
    // a failed fetch is simply skipped and the next poll tries again.
    return null;
  }
}

export function useBackendVersionChanged(apiUrl: string): boolean {
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    let loaded: string | null = null;
    let cancelled = false;

    const check = async () => {
      const version = await fetchVersion(apiUrl);
      if (cancelled || version === null) return;
      if (loaded === null) {
        // The first answer is the version this page runs against, whether it
        // came from the load-time fetch or (if that failed) a later poll.
        loaded = version;
        localStorage.setItem(STORAGE_KEY, version);
      } else if (version !== loaded) {
        localStorage.setItem(STORAGE_KEY, version);
        setChanged(true);
        // Nothing left to find out: the page is stale until it reloads.
        window.clearInterval(interval);
      }
    };

    check();
    const interval = window.setInterval(check, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [apiUrl]);

  return changed;
}
